import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, WMSTileLayer, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import { useSelector, useDispatch } from "react-redux";
import L from "leaflet";
import "leaflet.vectorgrid";
import "leaflet-side-by-side";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "leaflet-easyprint";
import "leaflet-velocity";

// Redux state selectors
import { selectLayerDataSet } from "../../../features/layers/overlaylayerslice";
import { selectBaseDataSet } from "../../../features/layers/baselayerslice";
import { selectMapZoomstate } from "../../../features/maps/mapZoomSlice";
import { selectTools, setTools } from "../../../features/tools/toolsSlice";
import { selectcqlData, setcqlData } from "../../../features/layers/cqlDataSlice";

// Map components
import Overlays from "./Overlays";
import Measure from "../ToolsWrapper/Measure";
import ProxyToolsEdit from "../ToolsWrapper/ProxyToolsEdit";
import Filter from "../ToolsWrapper/Filter";
import Meteogram from "./Meteogram";
import VerticalProfile from "./VerticalProfile";
import TFIDiagram from "./TFIDiagram";
import Legend from "./Legend";
import VelocityLayer from "./VelocityLayer";
import GeopotentialLayer from "./GeopotentialLayer";
import TrendChart from "./TrendChart";

// Add reverse geocoding function
const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`
    );
    
    if (!response.ok) {
      throw new Error("Failed to fetch address");
    }
    
    const data = await response.json();
    const address = data.address;
    
    const cityName = 
      address.city || 
      address.town || 
      address.village || 
      address.county || 
      address.state_district ||
      "";
    
    const stateName = address.state || "";
    
    let locationName = "";
    if (cityName && stateName) {
      locationName = `${cityName}, ${stateName}`;
    } else if (cityName) {
      locationName = cityName;
    } else if (stateName) {
      locationName = stateName;
    } else {
      locationName = address.country || "Unknown location";
    }
    
    return locationName;
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    return "Location unavailable";
  }
};

const Map = ({ visibility }) => {
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const tools = useSelector(selectTools);
  const cqlData = useSelector(selectcqlData);
  const baseLayers = useSelector(selectBaseDataSet);
  const overlayLayers = useSelector(selectLayerDataSet);
  const mapZoomState = useSelector(selectMapZoomstate);
  
  const [showDetails, setShowDetails] = useState(false);
  const [selectedView, setSelectedView] = useState("meteogram");
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [output, setOutput] = useState(null);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [mainData, setMainData] = useState(null);
  const [showTrend, setShowTrend] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(4);
  const markerRef = useRef(null);

  // Smooth Hover State & Ref
  const [hoverData, setHoverData] = useState({ show: false, name: "", x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);

  const isWbgtLayer = () => {
    return mainData && mainData.datatype === "wbgt";
  };

  const isMithunaGLB = () => {
    return mainData && mainData.text === "Mithuna GLB";
  };

  const getUnit = () => {
    if (!mainData || !mainData.datatype) return "";
    
    const datatype = mainData.datatype.toLowerCase();
    
    if (isMithunaGLB() && mainData.legend) {
      switch(datatype) {
        case 'temperature': return mainData.legend.temperature?.units || '°C';
        case 'humidity': return mainData.legend.humidity?.units || '%';
        case 'rainfall': return mainData.legend.rainfall?.units || 'mm';
        case 'acurain': return mainData.legend.acurain?.units || 'cm';
        case 'wind': return mainData.legend.wind?.units || 'm/s';
        default: return '';
      }
    }
    
    switch(datatype) {
      case 'temperature': return '°C';
      case 'humidity': return '%';
      case 'rainfall': return 'mm';
      case 'acurain':
      case 'accumulated_rainfall': return 'cm';
      case 'wind': return 'm/s';
      case 'wbgt': return '°C';
      default: return '';
    }
  };

  const formatOutput = (value, unit) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    switch(unit) {
      case '°C': return `${numValue.toFixed(1)} °C`;
      case '%': return `${Math.round(numValue)} %`;
      case 'mm': return `${numValue.toFixed(1)} mm`;
      case 'cm': return `${numValue.toFixed(1)} cm`;
      case 'm/s': return `${numValue.toFixed(1)} m/s`;
      default: return `${numValue.toFixed(1)} ${unit}`;
    }
  };

  const ZoomController = () => {
    const map = useMap();
    
    useEffect(() => {
      if (isWbgtLayer()) {
        map.setMinZoom(5);
        if (map.getZoom() < 5) map.setZoom(5);
        
        const handleZoom = () => {
          const currentZ = map.getZoom();
          setCurrentZoom(currentZ);
          if (currentZ < 5) map.setZoom(5);
        };
        
        map.on('zoom', handleZoom);
        return () => {
          map.off('zoom', handleZoom);
          map.setMinZoom(4);
        };
      } else {
        map.setMinZoom(4);
      }
    }, [map, isWbgtLayer()]);
    
    return null;
  };

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.leafletElement;
      if (showDetails || showTrend) {
        map.dragging.disable();
        map.doubleClickZoom.disable();
        map.scrollWheelZoom.disable();
      } else {
        map.dragging.enable();
        map.doubleClickZoom.enable();
        map.scrollWheelZoom.enable();
        
        if (isWbgtLayer()) {
          map.setZoom(5);
          map.setMinZoom(5);
        }
      }
    }
  }, [showDetails, showTrend, isWbgtLayer()]);

  useEffect(() => {
    if (Array.isArray(overlayLayers)) {
      const modelLayer = overlayLayers.find((layer) => layer.show && layer.class === "Model");
      setMainData(modelLayer || null);
      
      if (modelLayer && modelLayer.datatype === "wbgt" && mapRef.current) {
        const map = mapRef.current.leafletElement;
        map.setZoom(5);
        map.setMinZoom(5);
      }
    }
  }, [overlayLayers]);

  useEffect(() => {
    if (markerRef.current) {
      setShowDetails(false);
      setShowTrend(false);
      dispatch(setcqlData({ show: false }));
      markerRef.current.openPopup();
    }
  }, [location]);

  const handleDetailsClose = () => {
    setShowDetails(false);
    setShowTrend(false);
  };

  const handleViewMoreClick = (e) => {
    e.stopPropagation();
    setShowDetails(true);
    dispatch(setcqlData({ show: true }));
  };

  const handleTrendClick = (e) => {
    e.stopPropagation();
    setShowTrend(true);
    dispatch(setcqlData({ show: true }));
  };

  const shouldShowVerticalProfile = () => {
    if (!mainData || !mainData.date) return false;
    
    const dateParts = mainData.date.split('-');
    if (dateParts.length < 4) return false;
    
    const hour = dateParts[4];
    const validHours = ['00', '06', '12', '18', '24'];
    return validHours.includes(hour);
  };

  const renderSelectedView = () => {
    if (isWbgtLayer()) {
      return <TrendChart position={position} location={location} onClose={handleDetailsClose} />;
    }
    
    switch (selectedView) {
      case "meteogram":
        return <Meteogram position={position} location={location} onClose={handleDetailsClose} />;
      case "verticalProfile":
        return <VerticalProfile position={position} location={location} onClose={handleDetailsClose} />;
      case "tfiDiagram":
        return <TFIDiagram position={position} location={location} onClose={handleDetailsClose} />;
      default:
        return <Meteogram position={position} location={location} onClose={handleDetailsClose} />;
    }
  };

  const HandleClick = () => {
    const tools = useSelector(selectTools);
    
    useMapEvents({
      click: async (e) => {
        // Clear hover popup immediately on click
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setHoverData(prev => ({ ...prev, show: false }));

        const isToolActive = tools.some(tool => 
          (tool.name === "Measure" || tool.name === "Proximity" || tool.name === "Filter") && tool.show
        );
        
        if (showDetails || showTrend ||
          e.originalEvent.target.closest(".leaflet-popup") || 
          e.originalEvent.target.closest(".custom-ui") || 
          e.originalEvent.target.tagName === "BUTTON" ||
          isToolActive
        ) {
          return;
        }

        const { lat, lng } = e.latlng;
        setLocation({ lat, lng });
        setPosition({ x: e.originalEvent.clientX, y: e.originalEvent.clientY });
        
        const name = await reverseGeocode(lat, lng);
        setLocationName(name);

        if (!mainData || mainData.class !== "Model") return;

        let url;
        if (mainData.text === "Regional Model") {
          url = `https://api.ncmrwf.gov.in/${mainData.datatype.toLowerCase()}RegionalCoords?date=${mainData.date}&odate=${mainData.odate}&pressure=${mainData.altitude}&lat=${lat}&lon=${lng}&hour=${mainData.hours}`;
        } else {
          url = `https://api.ncmrwf.gov.in/${mainData.datatype.toLowerCase()}Coords?date=${mainData.date}&odate=${mainData.odate}&pressure=${mainData.altitude}&lat=${lat}&lon=${lng}&hour=${mainData.hours}`;
        }

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error("Failed to fetch data");
          const data = await response.json();
          setOutput(data.output);
        } catch (err) {
          console.error(err);
        }
      },
      
      mousemove: (e) => {
        // ADDED `|| location` HERE: Do not show hover if detail view, trend view, OR a clicked location is active
        if (showDetails || showTrend || location) return;
        
        // Instantly vanish the previous hover tooltip on move
        if (hoverData.show) {
          setHoverData(prev => ({ ...prev, show: false }));
        }
        
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        
        // Start a small 350ms delay to detect deliberate hovering
        hoverTimeoutRef.current = setTimeout(async () => {
          const { lat, lng } = e.latlng;
          const name = await reverseGeocode(lat, lng);
          
          const clientX = e.originalEvent.clientX || (e.originalEvent.touches && e.originalEvent.touches[0].clientX);
          const clientY = e.originalEvent.clientY || (e.originalEvent.touches && e.originalEvent.touches[0].clientY);

          setHoverData({
            show: true,
            name,
            x: clientX,
            y: clientY
          });
        }, 350);
      },
      
      mousedown: () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setHoverData(prev => ({ ...prev, show: false }));
      },
      
      mouseout: () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setHoverData(prev => ({ ...prev, show: false }));
      }
    });

    return null;
  };

  const ZoomToLocation = ({ myset }) => {
    const map = useMap();
    const prevLocation = useRef({ lat: null, lng: null, zoom: null });
    
    useEffect(() => {
      if (myset.lat && myset.lng) {
        const newLocation = { lat: myset.lat, lng: myset.lng };
        const newZoom = myset.zoom;
        if (
          prevLocation.current.lat !== newLocation.lat ||
          prevLocation.current.lng !== newLocation.lng ||
          prevLocation.current.zoom !== newZoom
        ) {
          map.flyTo([newLocation.lat, newLocation.lng], newZoom);
          prevLocation.current = { ...newLocation, zoom: newZoom };
        }
      }
    }, [myset, map]);
    
    return null;
  };

  useEffect(() => {
    if (cqlData && cqlData.lat && cqlData.lng && (cqlData.show === "yes" || cqlData.show === "yes1")) {
      const { lat, lng } = cqlData;
      setLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      dispatch(setcqlData({ show: false }));
    }
  }, [cqlData, dispatch]);

  const handlePopupClose = () => {
    if (markerRef.current) {
      markerRef.current.remove();
      setLocation(null);
      setShowTrend(false);
      setLocationName("");
      
      // Also ensure hover state is cleared just in case
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      setHoverData(prev => ({ ...prev, show: false }));
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapContainer 
        center={[22.5937, 78.9629]} 
        zoom={4} 
        zoomControl={true} 
        maxZoom={12} 
        minZoom={4} 
        ref={mapRef} 
        attributionControl={false}
      >
        <ZoomController />
        <Legend />
        <VelocityLayer />
        <GeopotentialLayer />

        {baseLayers.map((baselayer, index) =>
          baselayer.show ? (
            baselayer.type === "tile" ? (
              <TileLayer key={index} url={baselayer.link} zIndex="1" maxZoom={20} />
            ) : (
              <WMSTileLayer 
                key={index} 
                url={baselayer.link} 
                layers={baselayer.layer} 
                format="image/png" 
                zIndex="1" 
                maxZoom={20} 
              />
            )
          ) : null
        )}

        {overlayLayers.map((overlayer, index) => 
          overlayer.show && <Overlays key={index} overlayer={overlayer} index={0} />
        )}

        {showDetails && location && renderSelectedView()}
        {showTrend && location && isWbgtLayer() && <TrendChart position={position} location={location} onClose={handleDetailsClose} />}

        {location && mainData?.class === "Model" && mainData?.show && (
          <Marker 
            position={location} 
            ref={markerRef} 
            eventHandlers={{ popupclose: handlePopupClose }}
          >
            <Popup autoClose={false} closeOnClick={false}>
              <div>
                {locationName && (
                  <p style={{ margin: '0 0 12px 0', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    📍 {locationName}
                  </p>
                )}
                
                <p><strong>{mainData?.datatype.toUpperCase()}:</strong> {formatOutput(output, getUnit())}</p>
                
                {isWbgtLayer() ? (
                  <div>
                    <button className="custom-ui" onClick={handleTrendClick} style={{ fontSize: "12px", padding: "8px 16px", borderRadius: "5px", cursor: "pointer", backgroundColor: "#28a745", color: "white", border: "none", width: "100%", marginTop: "10px" }}>
                      View Trend
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ margin: "10px 0" }}>
                      <label style={{ display: "block", marginBottom: "5px" }}>
                        <input type="radio" name="viewType" value="meteogram" checked={selectedView === "meteogram"} onChange={() => setSelectedView("meteogram")} /> Meteogram
                      </label>
                      
                      {shouldShowVerticalProfile() && (
                        <label style={{ display: "block", marginBottom: "5px" }}>
                          <input type="radio" name="viewType" value="verticalProfile" checked={selectedView === "verticalProfile"} onChange={() => setSelectedView("verticalProfile")} /> Vertical Profile
                        </label>
                      )}
                      
                      <label style={{ display: "block", marginBottom: "5px" }}>
                        <input type="radio" name="viewType" value="tfiDiagram" checked={selectedView === "tfiDiagram"} onChange={() => setSelectedView("tfiDiagram")} /> EPSgram
                      </label> 
                    </div>

                    <button className="custom-ui" onClick={handleViewMoreClick} style={{ fontSize: "10px", padding: "5px 10px", borderRadius: "5px", cursor: "pointer", backgroundColor: "#007BFF", color: "white", border: "none", width: "100%" }}>
                      View Details
                    </button>
                  </div>
                )}
              </div>
            </Popup> 
          </Marker>
        )}

        {tools.filter((e) => e.name === "Measure")[0].show && <Measure />}
        {tools.filter((e) => e.name === "Proximity")[0].show && <ProxyToolsEdit />}
        {tools.filter((e) => e.name === "Filter")[0].show && <Filter />}
        
        {cqlData && cqlData.show === "yes" && <ZoomToLocation myset={{ lat: cqlData.lat, lng: cqlData.lng, zoom: 8 }} />}
        {cqlData && cqlData.show === "yes1" && <ZoomToLocation myset={{ lat: cqlData.lat, lng: cqlData.lng, zoom: 5 }} />}

        <HandleClick />
      </MapContainer>

      {/* Floating Tooltip Component */}
      {hoverData.show && hoverData.name && (
        <div
          style={{
            position: 'fixed',
            top: hoverData.y - 45, // Hovers just above the cursor 
            left: hoverData.x,
            transform: 'translateX(-50%)',
            backgroundColor: 'white',
            padding: '6px 14px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderLeft: '4px solid #007BFF',
            fontSize: '13px',
            fontWeight: '600',
            color: '#333',
            zIndex: 9999,
            pointerEvents: 'none', // Prevents tooltip from blocking map clicks
            animation: 'fadeIn 0.2s ease-in-out',
            whiteSpace: 'nowrap'
          }}
        >
          📍 {hoverData.name}
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translate(-50%, 10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
};

export default Map;